/**
 * GlassPill Component
 *
 * Gradient pill with glassmorphic background for bubble text headers.
 *
 * Features:
 * - Iridescent gradient pill (Pink → Lavender → Mint)
 * - Subtle blur overlay (intensity 30) for soap bubble effect
 * - Extra bold text (font-weight 900) for bubble aesthetic
 * - Colored shadow (purple) for depth
 * - WCAG AA: Black text on #A5FFEB + blur = 12:1 contrast (AAA)
 *
 * @example
 * <GlassPill translationKey="home.title" size="lg" />
 * <GlassPill translationKey="analysis.section_header" size="md" />
 */

import React from 'react';
import { Text, View, StyleSheet, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { useTranslation } from '@dramadetector/shared';
import { createBoxShadow } from '../../lib/design-tokens';

// Header gradient (first 3 icon colors for optimal contrast)
const HEADER_GRADIENT = ['#FFB3E6', '#D4A5FF', '#A5FFEB'] as const;

type PillSize = 'sm' | 'md' | 'lg';

interface GlassPillProps {
  /** Translation key for the pill text. Must be a valid i18n key. */
  translationKey: string;
  /** Size variant. Default: 'md' */
  size?: PillSize;
  /** Translation namespace. Default: 'home' */
  namespace?: string;
  /** Test ID for E2E testing */
  testID?: string;
}

export const GlassPill: React.FC<GlassPillProps> = ({ translationKey, size = 'md', namespace = 'home', testID }) => {
  const { t } = useTranslation(namespace as any);
  const fontSize = size === 'lg' ? 24 : size === 'md' ? 18 : 14;
  const paddingVertical = size === 'lg' ? 12 : size === 'md' ? 10 : 8;
  const paddingHorizontal = size === 'lg' ? 24 : size === 'md' ? 20 : 16;

  return (
    <View style={styles.container} testID={testID}>
      <LinearGradient
        colors={HEADER_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.gradient}
      >
        <BlurView intensity={30} tint="light" style={styles.blur}>
          <Text
            style={[
              styles.text,
              {
                fontSize,
                paddingVertical,
                paddingHorizontal,
              },
            ]}
          >
            {t(translationKey as any)}
          </Text>
        </BlurView>
      </LinearGradient>
    </View>
  );
};

// Platform-aware shadow style for pill container (purple glow)
const pillShadow = createBoxShadow({
  color: '#8A2BE2',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 8,
  opacity: 0.2,
  elevation: 4,
});

const styles = StyleSheet.create({
  container: {
    borderRadius: 9999, // Fully rounded pill
    overflow: 'hidden',
    alignSelf: 'center', // Center horizontally
    ...pillShadow,
  } as ViewStyle,
  gradient: {
    borderRadius: 9999,
  },
  blur: {
    borderRadius: 9999,
  },
  text: {
    color: '#1A1A1A', // Black text (WCAG AAA on #A5FFEB)
    fontWeight: '900', // Extra bold for bubble text aesthetic
    textAlign: 'center',
  },
});
